import React, { useState, useRef } from 'react';
import { MdDownload, MdUpload, MdAdd, MdClose, MdSearch, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { hfService } from '../../services/hfService';
import { useAuth } from '../../context/AuthContext';

const DataManagePage = ({ analyticsParams, fundList, onDataChanged }) => {
  const { user } = useAuth();
  const canCommit = !!user?.is_admin || !!user?.profile?.is_hf_admin;  // superuser OR HF admin (backend-gated too)
  // Step 1: Fund selection
  const [selectedExisting, setSelectedExisting] = useState([]);
  const [newFunds, setNewFunds] = useState([]);
  const [newName, setNewName] = useState('');
  const [newStrategy, setNewStrategy] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Step 2: Template
  const [generating, setGenerating] = useState(false);

  // Step 3: Upload
  const [uploadFile, setUploadFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  // Direct Bloomberg NAV upload (separate panel)
  const [navFile, setNavFile] = useState(null);
  const [navPreview, setNavPreview] = useState(null);
  const [navPreviewing, setNavPreviewing] = useState(false);
  const [navMode, setNavMode] = useState('upsert');   // 'upsert' | 'replace'
  const [navCommitting, setNavCommitting] = useState(false);
  const [navJob, setNavJob] = useState(null);         // polled job status
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const navFileRef = useRef(null);

  // Benchmark (S&P 500 total-return index) upload — two columns: date + index level.
  const [benchFile, setBenchFile] = useState(null);
  const [benchPreview, setBenchPreview] = useState(null);
  const [benchBusy, setBenchBusy] = useState(false);

  const handleNavPreview = async () => {
    if (!navFile || !datasetId) return;
    setNavPreviewing(true);
    setNavPreview(null);
    setNavJob(null);
    try {
      const res = await hfService.previewNavUpload(datasetId, navFile);
      setNavPreview(res.data);
    } catch (err) {
      alert('NAV preview failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setNavPreviewing(false);
    }
  };

  const pollNavJob = (jobId) => {
    const tick = async () => {
      try {
        const res = await hfService.getNavUploadJob(jobId);
        setNavJob(res.data);
        if (res.data.status === 'done' || res.data.status === 'failed') {
          setNavCommitting(false);
          if (res.data.status === 'done') onDataChanged && onDataChanged();
          return;
        }
      } catch (err) {
        // Transient poll error — keep trying; the job runs server-side regardless.
      }
      setTimeout(tick, 2000);
    };
    tick();
  };

  const startNavCommit = async () => {
    if (!navFile || !datasetId) return;
    setShowReplaceConfirm(false);
    setNavCommitting(true);
    setNavJob({ status: 'pending', processed_funds: 0, total_funds: 0 });
    try {
      const res = await hfService.commitNavUpload(datasetId, navFile, navMode);
      pollNavJob(res.data.job_id);
    } catch (err) {
      setNavCommitting(false);
      setNavJob(null);
      alert('NAV commit failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleNavCommit = () => {
    if (navMode === 'replace') {
      setShowReplaceConfirm(true);   // destructive — confirm first
    } else {
      startNavCommit();
    }
  };

  const handleBenchPreview = async () => {
    if (!benchFile || !datasetId) return;
    setBenchBusy(true); setBenchPreview(null);
    try {
      const res = await hfService.previewBenchmarkUpload(datasetId, benchFile);
      setBenchPreview(res.data);
    } catch (err) {
      alert('Benchmark preview failed: ' + (err.response?.data?.error || err.message));
    } finally { setBenchBusy(false); }
  };

  const handleBenchCommit = async () => {
    if (!benchFile || !datasetId) return;
    setBenchBusy(true);
    try {
      const res = await hfService.commitBenchmarkUpload(datasetId, benchFile);
      const d = res.data;
      alert(`Benchmark updated: ${d.created} new + ${d.updated} changed months `
        + `(${d.range?.[0]} → ${d.range?.[1]}). ${d.total_months} months total.`);
      setBenchPreview(null); setBenchFile(null);
      onDataChanged && onDataChanged();
    } catch (err) {
      alert('Benchmark commit failed: ' + (err.response?.data?.error || err.message));
    } finally { setBenchBusy(false); }
  };

  const datasetId = analyticsParams?.dataset_id;
  const allFundNames = (fundList || []).map(f => f.display_name || f);
  const strategies = [...new Set((fundList || []).map(f => f.strategy).filter(Boolean))].sort();

  const filteredFunds = searchQuery
    ? allFundNames.filter(f => f.toLowerCase().includes(searchQuery.toLowerCase()))
    : allFundNames;

  const toggleExisting = (name) => {
    setSelectedExisting(prev =>
      prev.includes(name) ? prev.filter(f => f !== name) : [...prev, name]
    );
  };

  // Select-all respects the active search filter
  const allFilteredSelected = filteredFunds.length > 0 && filteredFunds.every(f => selectedExisting.includes(f));
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedExisting(prev => prev.filter(f => !filteredFunds.includes(f)));
    } else {
      setSelectedExisting(prev => [...new Set([...prev, ...filteredFunds])]);
    }
  };

  const addNewFund = () => {
    if (!newName.trim()) return;
    setNewFunds(prev => [...prev, { name: newName.trim(), strategy: newStrategy }]);
    setNewName('');
    setNewStrategy('');
  };

  const removeNewFund = (idx) => {
    setNewFunds(prev => prev.filter((_, i) => i !== idx));
  };

  // Generate template
  const handleGenerate = async () => {
    if (!datasetId || (selectedExisting.length === 0 && newFunds.length === 0)) return;
    setGenerating(true);
    try {
      const res = await hfService.generateTemplate(datasetId, selectedExisting, newFunds);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'maghaweer_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate template: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  // Preview upload
  const handlePreview = async () => {
    if (!uploadFile || !datasetId) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await hfService.previewUpload(datasetId, uploadFile);
      setPreview(res.data);
    } catch (err) {
      alert('Preview failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPreviewing(false);
    }
  };

  // Commit upload
  const handleCommit = async () => {
    if (!uploadFile || !datasetId) return;
    setCommitting(true);
    try {
      const res = await hfService.commitUpload(datasetId, uploadFile);
      setResult(res.data);
      setPreview(null);
      setUploadFile(null);
      if (fileRef.current) fileRef.current.value = '';
      // Refresh parent fund list so new data shows immediately
      if (onDataChanged) onDataChanged();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setCommitting(false);
    }
  };

  const cardCls = "bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4";

  return (
    <div>
      <h2 className="text-lg font-serif font-semibold text-gray-800 dark:text-gray-200 mb-4">Data Management</h2>

      {/* Step 1: Select Funds */}
      <div className={cardCls}>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">1. Select Funds</h3>

        {/* Existing funds */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block">
              Update Existing Funds ({selectedExisting.length} selected)
            </label>
            <button
              onClick={toggleSelectAll}
              disabled={filteredFunds.length === 0}
              className="text-xs font-medium text-brand-gold hover:underline disabled:opacity-40"
            >
              {allFilteredSelected ? 'Clear all' : `Select all${searchQuery ? ' matches' : ''} (${filteredFunds.length})`}
            </button>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <MdSearch className="text-gray-400" />
            <input
              type="text"
              placeholder="Search existing funds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 outline-none border-b border-gray-200 dark:border-gray-700 pb-1 placeholder-gray-400"
            />
          </div>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            {filteredFunds.map(f => {
              const checked = selectedExisting.includes(f);
              return (
                <div
                  key={f}
                  onClick={() => toggleExisting(f)}
                  className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 text-sm text-gray-800 dark:text-gray-200"
                >
                  {checked ? <MdCheckBox className="text-brand-gold shrink-0" /> : <MdCheckBoxOutlineBlank className="text-gray-400 shrink-0" />}
                  <span className="truncate">{f}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* New funds */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 block">
            Add New Funds
          </label>
          <div className="flex gap-2 mb-2 flex-wrap">
            <input
              type="text"
              placeholder="Fund name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1 min-w-[150px] bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 outline-none"
            />
            <select
              value={newStrategy}
              onChange={(e) => setNewStrategy(e.target.value)}
              className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 outline-none"
            >
              <option value="">Strategy (optional)</option>
              {strategies.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              onClick={addNewFund}
              disabled={!newName.trim()}
              className="px-3 py-1.5 rounded-lg bg-brand-gold text-white text-sm font-medium disabled:opacity-40"
            >
              <MdAdd className="inline" /> Add
            </button>
          </div>
          {newFunds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {newFunds.map((nf, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                  {nf.name}{nf.strategy && ` (${nf.strategy})`}
                  <MdClose className="cursor-pointer hover:text-red-500" onClick={() => removeNewFund(i)} />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Step 2: Generate Template */}
      <div className={cardCls}>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">2. Download Template</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Generate a pre-filled Excel template with the selected funds. Fill in monthly returns as decimals (e.g., 0.05 = 5%).
        </p>
        <button
          onClick={handleGenerate}
          disabled={generating || (selectedExisting.length === 0 && newFunds.length === 0)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gold text-white text-sm font-medium disabled:opacity-40 hover:bg-brand-gold-dark transition-colors"
        >
          <MdDownload />
          {generating ? 'Generating...' : 'Generate & Download Template'}
        </button>
      </div>

      {/* Step 3: Upload Filled Template */}
      <div className={cardCls}>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">3. Upload Filled Template</h3>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setUploadFile(e.target.files[0]); setPreview(null); setResult(null); }}
          className="block w-full text-sm text-gray-800 dark:text-gray-200 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-200 dark:file:border-gray-700 file:text-sm file:bg-white dark:file:bg-[#0d1117] file:text-gray-700 dark:file:text-gray-300 mb-3"
        />

        {uploadFile && !preview && !result && (
          <button
            onClick={handlePreview}
            disabled={previewing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            {previewing ? 'Analyzing...' : 'Preview Changes'}
          </button>
        )}

        {/* Preview results */}
        {preview && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Preview</h4>

            {preview.creates?.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">New Funds to Create:</div>
                {preview.creates.map((c, i) => (
                  <div key={i} className="text-sm text-gray-700 dark:text-gray-300 pl-3">
                    + {c.name} ({c.months} months, {c.date_start} to {c.date_end}){c.strategy && ` [${c.strategy}]`}
                  </div>
                ))}
              </div>
            )}

            {preview.updates?.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Existing Funds to Update:</div>
                {preview.updates.map((u, i) => (
                  <div key={i} className="text-sm text-gray-700 dark:text-gray-300 pl-3">
                    ~ {u.name}: +{u.months_to_add} months ({u.date_start} to {u.date_end})
                    {u.current_last_date && ` (current last: ${u.current_last_date})`}
                    {!u.exists && <span className="text-red-500 ml-1">NOT FOUND</span>}
                  </div>
                ))}
              </div>
            )}

            {(preview.creates?.length > 0 || preview.updates?.some(u => u.exists)) && (
              <button
                onClick={handleCommit}
                disabled={committing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gold text-white text-sm font-medium hover:bg-brand-gold-dark transition-colors mt-3"
              >
                <MdUpload />
                {committing ? 'Uploading...' : 'Confirm & Upload'}
              </button>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2">Upload Complete</div>
            {result.created?.map((c, i) => (
              <div key={i} className="text-sm text-green-600 dark:text-green-400">Created: {c.name} ({c.months} months)</div>
            ))}
            {result.updated?.map((u, i) => (
              <div key={i} className="text-sm text-blue-600 dark:text-blue-400">Updated: {u.name} (+{u.months_added} new, {u.months_updated} replaced)</div>
            ))}
            {result.errors?.map((e, i) => (
              <div key={i} className="text-sm text-red-500">Error: {e}</div>
            ))}
          </div>
        )}
      </div>

      {/* ============ Direct Bloomberg NAV Upload ============ */}
      <div className="border-t border-gray-200 dark:border-gray-800 my-6" />
      <div className={cardCls}>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">Direct NAV Upload (Bloomberg MV)</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Upload your Bloomberg MV export directly — funds as rows, month-end dates as columns, raw NAV levels in the grid.
          Funds are matched to this dataset by BBG ticker (then name); NAVs are converted to monthly returns. Preview first, then commit.
        </p>
        <input
          ref={navFileRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setNavFile(e.target.files[0]); setNavPreview(null); }}
          className="block w-full text-sm text-gray-800 dark:text-gray-200 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-200 dark:file:border-gray-700 file:text-sm file:bg-white dark:file:bg-[#0d1117] file:text-gray-700 dark:file:text-gray-300 mb-3"
        />
        {/* Write mode — what a commit does to each matched fund */}
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Write mode</div>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {[
              { key: 'upsert', label: 'Upsert' },
              { key: 'replace', label: 'Replace' },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setNavMode(m.key)}
                disabled={navCommitting}
                className={`px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 ${
                  navMode === m.key
                    ? (m.key === 'replace'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900')
                    : 'bg-white dark:bg-[#0d1117] text-gray-600 dark:text-gray-300'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            {navMode === 'upsert'
              ? 'Insert new dates, update changed values, and keep existing dates not in the file.'
              : 'For each matched fund, DELETE all existing NAVs and replace them with the file’s full history (use for a currency switch).'}
          </p>
        </div>

        {navFile && (
          <button
            onClick={handleNavPreview}
            disabled={navPreviewing || navCommitting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-40"
          >
            {navPreviewing ? 'Analyzing file...' : 'Preview NAV Upload'}
          </button>
        )}

        {navPreview && (
          <div className="mt-4 space-y-4">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Funds in file</div>
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">{navPreview.total_funds_in_file}</div>
              </div>
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
                <div className="text-xs text-blue-600 dark:text-blue-400">Matched (will update)</div>
                <div className="text-lg font-semibold text-blue-700 dark:text-blue-300">{navPreview.matched_count}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Unmatched (skipped)</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{navPreview.unmatched_count}</div>
              </div>
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                <div className="text-xs text-amber-600 dark:text-amber-400">Flagged for review</div>
                <div className="text-lg font-semibold text-amber-700 dark:text-amber-300">{navPreview.flagged_fund_count}</div>
              </div>
            </div>

            {/* Data diff vs what's already stored (one point = one fund-month NAV) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
                <div className="text-xs text-green-600 dark:text-green-400">New data points</div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-300">{navPreview.new_points?.toLocaleString()}</div>
                <div className="text-[10px] text-gray-400">{navPreview.new_data_fund_count} funds</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Unchanged (matching)</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{navPreview.unchanged_points?.toLocaleString()}</div>
              </div>
              <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-3">
                <div className="text-xs text-orange-600 dark:text-orange-400">Changed (restated)</div>
                <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">{navPreview.changed_points?.toLocaleString()}</div>
                <div className="text-[10px] text-gray-400">{navPreview.modified_fund_count} funds modified</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Total file points</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  {((navPreview.new_points || 0) + (navPreview.unchanged_points || 0) + (navPreview.changed_points || 0)).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Restatements — every existing NAV point that changed, with delta */}
            {navPreview.restatements?.length > 0 && (
              <div>
                <div className="text-xs font-medium text-orange-600 dark:text-orange-400 mb-1">
                  Restatements — every changed NAV ({navPreview.changed_points} points across {navPreview.modified_fund_count} funds)
                  {navPreview.restatements_truncated && (
                    <span className="text-gray-400 italic"> · showing first {navPreview.restatements.length}</span>
                  )}
                </div>
                <div className="max-h-80 overflow-auto scrollbar-thin border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-[#0d1117] text-gray-500 dark:text-gray-400">
                      <tr className="text-left">
                        <th className="px-2 py-1.5 font-medium">Fund</th>
                        <th className="px-2 py-1.5 font-medium">Date</th>
                        <th className="px-2 py-1.5 font-medium text-right">Old</th>
                        <th className="px-2 py-1.5 font-medium text-right">New</th>
                        <th className="px-2 py-1.5 font-medium text-right">Δ</th>
                        <th className="px-2 py-1.5 font-medium text-right">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {navPreview.restatements.map((r, i) => (
                        <tr key={i} className="text-gray-700 dark:text-gray-300">
                          <td className="px-2 py-1 max-w-[160px] truncate" title={r.name}>{r.name}</td>
                          <td className="px-2 py-1 whitespace-nowrap text-gray-500 dark:text-gray-400">{r.date}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{r.old}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{r.new}</td>
                          <td className={`px-2 py-1 text-right tabular-nums ${r.delta >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {r.delta >= 0 ? '+' : ''}{r.delta}
                          </td>
                          <td className={`px-2 py-1 text-right tabular-nums ${(r.pct ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {r.pct == null ? '—' : `${r.pct >= 0 ? '+' : ''}${(r.pct * 100).toFixed(2)}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Flagged funds — data-quality review */}
            {navPreview.flagged?.length > 0 && (
              <div>
                <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
                  Large NAV moves (&gt;{Math.round(navPreview.outlier_threshold * 100)}% in a month) — review before committing
                  {' '}({navPreview.total_outlier_months} months across {navPreview.flagged_fund_count} funds)
                </div>
                <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
                  {navPreview.flagged.map((fl, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span className="truncate text-gray-700 dark:text-gray-300">
                        {fl.name} {!fl.matched && <span className="text-gray-400">(unmatched)</span>}
                      </span>
                      <span className="shrink-0 ml-2 text-xs text-gray-500 dark:text-gray-400">
                        {fl.outlier_months} mo · worst{' '}
                        <span className={fl.worst_return >= 0 ? 'text-green-500' : 'text-red-500'}>
                          {(fl.worst_return * 100).toFixed(1)}%
                        </span>{' '}({fl.worst_date})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unmatched funds — would be skipped */}
            {navPreview.unmatched?.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Unmatched funds (skipped — not in this dataset)
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
                  {navPreview.unmatched.map((u, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">
                      <span className="truncate">{u.name}</span>
                      <span className="shrink-0 ml-2 text-xs text-gray-400">{u.bbg_ticker}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Commit */}
            {canCommit ? (
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                {!navJob && (
                  <>
                    <button
                      onClick={handleNavCommit}
                      disabled={navCommitting || navPreview.matched_count === 0}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-40 ${
                        navMode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      {navMode === 'replace'
                        ? `Replace NAVs for ${navPreview.matched_count} funds`
                        : `Commit (Upsert) ${navPreview.matched_count} funds`}
                    </button>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Unmatched funds ({navPreview.unmatched_count}) are skipped — never auto-created.
                    </p>
                  </>
                )}

                {navJob && (
                  <div className="space-y-2">
                    {(navJob.status === 'pending' || navJob.status === 'running') && (
                      <>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {navJob.status === 'pending' ? 'Queued…' : 'Writing NAVs + recomputing returns & stats…'}
                          {' '}{navJob.processed_funds}/{navJob.total_funds || navPreview.matched_count}
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 transition-all"
                            style={{ width: `${navJob.total_funds ? Math.round((navJob.processed_funds / navJob.total_funds) * 100) : 5}%` }}
                          />
                        </div>
                      </>
                    )}

                    {navJob.status === 'done' && navJob.summary && (
                      <div>
                        <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mb-2">
                          Done — {navJob.summary.matched_funds} funds {navJob.summary.mode === 'replace' ? 'replaced' : 'upserted'}.
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
                            <div className="text-xs text-green-600 dark:text-green-400">New points</div>
                            <div className="text-lg font-semibold text-green-700 dark:text-green-300">{(navJob.summary.new_points || 0).toLocaleString()}</div>
                          </div>
                          <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-3">
                            <div className="text-xs text-orange-600 dark:text-orange-400">Changed</div>
                            <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">{(navJob.summary.changed_points || 0).toLocaleString()}</div>
                          </div>
                          {navJob.summary.mode === 'replace' && (
                            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                              <div className="text-xs text-red-600 dark:text-red-400">Removed</div>
                              <div className="text-lg font-semibold text-red-700 dark:text-red-300">{(navJob.summary.removed_points || 0).toLocaleString()}</div>
                            </div>
                          )}
                          <div className="rounded-lg bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400">Skipped (new)</div>
                            <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{(navJob.summary.skipped_new_funds || 0).toLocaleString()}</div>
                          </div>
                          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                            <div className="text-xs text-amber-600 dark:text-amber-400">Flagged</div>
                            <div className="text-lg font-semibold text-amber-700 dark:text-amber-300">{(navJob.summary.flagged_funds || 0).toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {navJob.status === 'failed' && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        Commit failed. {navJob.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                Preview only — committing NAVs requires admin (superuser) access.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============ Benchmark (S&P 500) Total-Return Index Upload ============ */}
      <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] p-5">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">Benchmark Upload (S&amp;P 500 Total-Return Index)</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Upload the S&amp;P 500 total-return INDEX — two columns: date (month-end) and the index level.
          Levels are converted to monthly returns and become this dataset&apos;s benchmark; the uploaded series
          wins on overlapping months. Preview the diff first, then commit.
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            type="file" accept=".xlsx,.xls,.csv"
            onChange={(e) => { setBenchFile(e.target.files?.[0] || null); setBenchPreview(null); }}
            className="text-sm text-gray-600 dark:text-gray-400"
          />
          <button
            onClick={handleBenchPreview} disabled={!benchFile || benchBusy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40"
          >
            {benchBusy ? 'Analyzing…' : 'Preview'}
          </button>
        </div>
        {benchPreview && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
                <div className="text-xs text-green-600 dark:text-green-400">New months</div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-300">{benchPreview.creates_count}</div>
              </div>
              <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-3">
                <div className="text-xs text-orange-600 dark:text-orange-400">Changed</div>
                <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">{benchPreview.updates_count}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Unchanged</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{benchPreview.unchanged_count}</div>
              </div>
            </div>
            <div className="text-[11px] text-gray-400">
              File covers {benchPreview.file_range?.[0]} → {benchPreview.file_range?.[1]}
              {benchPreview.existing_range && (
                <> · existing benchmark {benchPreview.existing_range[0]} → {benchPreview.existing_range[1]}</>
              )}
            </div>
            {benchPreview.updates?.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-[#0d1117] text-gray-400">
                    <tr>
                      <th className="text-left px-2 py-1">Month</th>
                      <th className="text-right px-2 py-1">Old</th>
                      <th className="text-right px-2 py-1">New</th>
                      <th className="text-right px-2 py-1">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {benchPreview.updates.slice(0, 200).map((u) => (
                      <tr key={u.date} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="px-2 py-1 text-gray-600 dark:text-gray-300">{u.date}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-500">{(u.old * 100).toFixed(2)}%</td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-700 dark:text-gray-200">{(u.new * 100).toFixed(2)}%</td>
                        <td className={`px-2 py-1 text-right tabular-nums ${u.delta >= 0 ? 'text-green-600' : 'text-red-500'}`}>{(u.delta * 100).toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {canCommit ? (
              <button
                onClick={handleBenchCommit} disabled={benchBusy}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
              >
                {benchBusy ? 'Committing…' : `Commit — ${benchPreview.creates_count} new + ${benchPreview.updates_count} changed`}
              </button>
            ) : (
              <div className="text-xs text-gray-400 italic">Preview only — committing the benchmark requires admin access.</div>
            )}
          </div>
        )}
      </div>

      {/* Destructive Replace confirmation */}
      {showReplaceConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 p-5 shadow-xl">
            <h4 className="text-base font-semibold text-red-600 dark:text-red-400 mb-2">Replace NAVs — destructive</h4>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              This DELETES all existing NAVs for {navPreview?.matched_count} matched funds and replaces them
              with the uploaded file. Returns &amp; stats are recomputed from scratch. This cannot be undone. Continue?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowReplaceConfirm(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={startNavCommit}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
              >
                Yes, replace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagePage;
